import EditIcon from "@mui/icons-material/Edit";
import { useRef } from "react";

interface OnboardingAvatarEditorProps {
  /** The first name initial to show when no avatar is uploaded. */
  initial: string;
  /** Preview URL of the uploaded avatar (null = show initial). */
  previewUrl: string | null;
  /** Called when the user picks a file. */
  onFileSelected: (file: File) => void;
}

/**
 * OnboardingAvatarEditor — circular avatar with an edit button overlay.
 *
 * Shows either the user's uploaded photo or a large initial letter inside
 * a light-dust circle. A small edit (pencil) button at bottom-right opens
 * the file picker. Matches the demo's `.cx-ob-avatar` pattern.
 */
export function OnboardingAvatarEditor({
  initial,
  previewUrl,
  onFileSelected,
}: OnboardingAvatarEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelected(file);
    }
  };

  return (
    <div className="gfg-ob-avatar">
      <div className="gfg-ob-avatar-preview">
        {previewUrl ? (
          <img src={previewUrl} alt="Your avatar" />
        ) : (
          <span className="gfg-ob-initial">{initial || "?"}</span>
        )}
      </div>
      <button
        type="button"
        className="gfg-ob-avatar-edit"
        title="Upload a photo"
        aria-label="Upload a photo"
        onClick={() => fileInputRef.current?.click()}
      >
        <EditIcon sx={{ fontSize: 15, color: "inherit" }} />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
}
